/**
 * «Заработок» — экран няни.
 *
 * Отвечает на вопрос, который иначе приходится задавать вслух: сколько уже
 * заработано и когда придут деньги. Пока период идёт — живая сумма. Когда
 * расчёт прислали — две честные кнопки: «Всё верно» и «Есть вопрос».
 */

import { useState } from 'react'

import { useCurrency, useFamily, useSession, useTimeZone } from '@/app/session'
import { Confetti } from '@/features/pay/Confetti'
import { downloadTimesheetCsv } from '@/features/pay/export'
import { TimesheetDetails } from '@/features/pay/TimesheetDetails'
import { TimesheetSummary } from '@/features/pay/TimesheetSummary'
import {
  STATUS_LABELS,
  useConfirmPayment,
  useCurrentTimesheet,
  useDisputeTimesheet,
  useTimesheet,
  useTimesheetHistory,
} from '@/shared/api/pay'
import { formatDuration } from '@/shared/format'
import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Screen } from '@/shared/ui/Screen'
import { Icon } from '@/shared/ui/Icon'
import { Sheet } from '@/shared/ui/Sheet'
import { Field, TextInput } from '@/shared/ui/fields'
import { Card, Chip, Money, Row, SectionTitle } from '@/shared/ui/primitives'
import { ErrorState, SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

export function Earnings() {
  const me = useSession((state) => state.me)
  const family = useFamily()
  const timeZone = useTimeZone()
  const currency = useCurrency()
  const familyId = family?.id ?? 0

  const [disputeOpen, setDisputeOpen] = useState(false)
  const [celebrate, setCelebrate] = useState(false)
  const [historyId, setHistoryId] = useState<number | null>(null)

  const current = useCurrentTimesheet(familyId)
  const history = useTimesheetHistory(familyId)

  const confirm = useConfirmPayment(familyId, {
    onSuccess: () => {
      haptic('success')
      // Единственный момент в приложении, который стоит отметить.
      setCelebrate(true)
      toast.success('Период закрыт')
    },
    onError: (error) => toast.error(error.message),
  })

  if (!me || !family) return null
  if (current.isLoading) {
    return (
      <Screen title="Заработок">
        <SkeletonCard lines={3} />
        <SkeletonCard lines={2} />
      </Screen>
    )
  }
  if (current.error) {
    return (
      <Screen title="Заработок">
        <ErrorState error={current.error} onRetry={() => void current.refetch()} />
      </Screen>
    )
  }

  const timesheet = current.data!
  const closed = (history.data?.items ?? []).filter((item) => item.id !== timesheet.id)
  const needsPayoutDetails = !me.user.phone

  return (
    <Screen title="Заработок">
      <TimesheetSummary timesheet={timesheet} />

      {timesheet.status === 'draft' && (
        <p className="text-text-muted px-1 text-caption leading-snug">
          Период ещё идёт — сумма растёт с каждой сменой. Родитель пришлёт расчёт, когда
          период закончится.
        </p>
      )}

      {needsPayoutDetails && (
        <Card className="bg-warm-soft">
          <p className="text-warm-ink text-body font-semibold">Не указан номер для перевода</p>
          <p className="text-text-muted mt-1 text-caption leading-snug">
            Родитель увидит его на экране оплаты и сможет скопировать в один тап. Заполняется
            в профиле, одно поле.
          </p>
        </Card>
      )}

      {timesheet.status === 'sent' && (
        <Card>
          <p className="text-body font-semibold">Расчёт прислали</p>
          <p className="text-text-muted mt-1 text-note leading-snug">
            Посмотрите детализацию ниже. Если всё сходится — так и скажите; если что-то
            не так, спросить нормально.
          </p>
          <div className="mt-4 space-y-2">
            <Button block size="lg" hapticKind="success" onClick={() => toast.success('Ждём перевод')}>
              Всё верно
            </Button>
            <Button block size="lg" variant="ghost" onClick={() => setDisputeOpen(true)}>
              Есть вопрос
            </Button>
          </div>
        </Card>
      )}

      {timesheet.status === 'paid_declared' && (
        <Card>
          <p className="text-body font-semibold">Родитель отметил перевод</p>
          <p className="text-text-muted mt-1 text-note leading-snug">
            Проверьте, пришли ли деньги. Подтверждайте только когда увидели их у себя —
            это и есть закрытие периода.
          </p>
          <div className="mt-4 space-y-2">
            <Button
              block
              size="lg"
              loading={confirm.isPending}
              hapticKind="success"
              onClick={() => confirm.mutate(timesheet.id)}
            >
              Деньги получила
            </Button>
            <Button block size="lg" variant="ghost" onClick={() => setDisputeOpen(true)}>
              Не пришли
            </Button>
          </div>
        </Card>
      )}

      {timesheet.status === 'disputed' && (
        <Card className="bg-warm-soft">
          <p className="text-warm-ink text-body font-semibold">Вопрос отправлен</p>
          <p className="mt-1 text-note leading-snug">{timesheet.dispute_note}</p>
          <p className="text-text-muted mt-2 text-caption">
            Родитель ответит и вернёт расчёт в работу.
          </p>
        </Card>
      )}

      {timesheet.status === 'confirmed' && (
        <Card className="bg-success-soft text-center">
          <p className="text-success flex items-center gap-1.5 text-body font-semibold">
            <Icon name="state-success" size={18} />
            Период закрыт
          </p>
          <p className="text-text-muted mt-1 text-note">Деньги получены, всё сошлось</p>
        </Card>
      )}

      <TimesheetDetails timesheet={timesheet} timeZone={timeZone} />

      {closed.length > 0 && (
        <div>
          <SectionTitle
            action={
              <span className="text-text-muted text-caption">
                {formatDuration(history.data?.total_minutes ?? 0)} ·{' '}
                {history.data?.total_paid ? (
                  <Money
                    value={history.data.total_paid}
                    currency={currency}
                    size="sm"
                    tone="muted"
                  />
                ) : null}
              </span>
            }
          >
            История
          </SectionTitle>
          <Card padded={false} className="px-4">
            {closed.map((item, index) => (
              <div key={item.id}>
                {index > 0 && <div className="bg-line h-px" />}
                <Row
                  title={item.period_title}
                  subtitle={formatDuration(item.minutes_total)}
                  right={
                    <span className="flex items-center gap-2">
                      {item.status !== 'confirmed' && (
                        <Chip tone="warm">{STATUS_LABELS[item.status]}</Chip>
                      )}
                      <Money value={item.amount_final} currency={item.currency} size="sm" />
                    </span>
                  }
                  onClick={() => setHistoryId(item.id)}
                />
              </div>
            ))}
          </Card>
        </div>
      )}

      <DisputeSheet
        open={disputeOpen}
        timesheetId={timesheet.id}
        familyId={familyId}
        onClose={() => setDisputeOpen(false)}
      />

      <HistorySheet
        familyId={familyId}
        timesheetId={historyId}
        timeZone={timeZone}
        onClose={() => setHistoryId(null)}
      />

      <Confetti show={celebrate} onDone={() => setCelebrate(false)} />
    </Screen>
  )
}

/** «Есть вопрос» — с текстом, а не молчаливым отказом. */
function DisputeSheet({
  open,
  timesheetId,
  familyId,
  onClose,
}: {
  open: boolean
  timesheetId: number
  familyId: number
  onClose: () => void
}) {
  const [reason, setReason] = useState('')

  const dispute = useDisputeTimesheet(familyId, {
    onSuccess: () => {
      toast.show('Вопрос отправлен родителю')
      setReason('')
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })

  return (
    <Sheet
      open={open}
      onClose={() => {
        setReason('')
        onClose()
      }}
      title="Что не сходится"
      footer={
        <Button
          block
          size="lg"
          loading={dispute.isPending}
          disabled={!reason.trim()}
          onClick={() => dispute.mutate({ id: timesheetId, reason: reason.trim() })}
        >
          Отправить вопрос
        </Button>
      }
    >
      <Field
        label="Опишите своими словами"
        hint="Спрашивать про деньги — нормально. Родитель увидит вопрос и вернётся с ответом."
      >
        <TextInput
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="13-го я работала на два часа дольше"
          maxLength={300}
          autoFocus
        />
      </Field>
    </Sheet>
  )
}

function HistorySheet({
  familyId,
  timesheetId,
  timeZone,
  onClose,
}: {
  familyId: number
  timesheetId: number | null
  timeZone: string
  onClose: () => void
}) {
  const [exporting, setExporting] = useState(false)
  const query = useTimesheet(familyId, timesheetId)
  const timesheet = query.data

  return (
    <Sheet
      open={timesheetId !== null}
      onClose={onClose}
      title={timesheet ? timesheet.period_title : 'Расчёт'}
      footer={
        timesheet ? (
          <Button
            block
            size="lg"
            variant="secondary"
            loading={exporting}
            onClick={async () => {
              setExporting(true)
              try {
                await downloadTimesheetCsv(
                  timesheet.id,
                  `yanyana_${timesheet.period_start}_${timesheet.period_end}.csv`,
                )
              } catch {
                toast.error('Ваш Telegram не даёт сохранить файл — откройте в браузере')
              } finally {
                setExporting(false)
              }
            }}
          >
            Выгрузить в CSV
          </Button>
        ) : undefined
      }
    >
      {query.isLoading && <SkeletonCard lines={3} />}
      {timesheet && (
        <div className="space-y-3">
          <TimesheetSummary timesheet={timesheet} />
          <TimesheetDetails timesheet={timesheet} timeZone={timeZone} />
        </div>
      )}
    </Sheet>
  )
}
